import { useEffect, useState } from "react";
import {
  Activity,
  Beaker,
  Check,
  ChevronRight,
  CircleGauge,
  Copy,
  Cpu,
  Database,
  Download,
  Eye,
  EyeOff,
  FileLock2,
  Gauge,
  KeyRound,
  Link2,
  LogOut,
  Network,
  RefreshCw,
  Save,
  Settings2,
  ShieldCheck,
  Sparkles,
  Trash2,
  Users,
  Wifi,
  X,
  Zap,
} from "lucide-react";
import type {
  AccountProfile,
  AppSettings,
  GpuCapability,
  InferenceMode,
  ModelDefinition,
  ModelLoadState,
  SwarmSession,
} from "../types";
import type { ConnectedPeer } from "../lib/swarm-client";
import { formatSwarmCode, generatePasscode, normalizeSwarmCode } from "../lib/crypto";
import { MODE_PROFILES } from "../models";

type SettingsTab = "general" | "account" | "context" | "model" | "swarm" | "acceleration" | "privacy";

interface SettingsDrawerProps {
  open: boolean;
  settings: AppSettings;
  model: ModelDefinition;
  loadState: ModelLoadState;
  gpu: GpuCapability | null;
  swarm: SwarmSession | null;
  peers: ConnectedPeer[];
  signalingRttMs: number | null;
  turnConfigured: boolean;
  inferenceMode: InferenceMode;
  contributing: boolean;
  account: AccountProfile | null;
  deviceName: string;
  onClose: () => void;
  onSettings: (settings: AppSettings) => void;
  onOpenModels: () => void;
  onLoadModel: () => void;
  onCreateSwarm: (deviceName: string) => Promise<void>;
  onJoinSwarm: (code: string, passcode: string, deviceName: string) => Promise<void>;
  onLeaveSwarm: () => Promise<void>;
  onInferenceMode: (value: InferenceMode) => void;
  onContributing: (value: boolean) => void;
  onSaveAccount: (displayName: string) => Promise<void>;
  onDeleteAccount: () => Promise<void>;
  onDeviceName: (value: string) => void;
  onClearData: () => Promise<void>;
}

const TABS: Array<{ id: SettingsTab; label: string; icon: typeof Settings2 }> = [
  { id: "general", label: "一般", icon: Settings2 },
  { id: "account", label: "アカウント", icon: KeyRound },
  { id: "context", label: "コンテキスト", icon: Sparkles },
  { id: "model", label: "AI", icon: Cpu },
  { id: "swarm", label: "共有", icon: Network },
  { id: "privacy", label: "安全性", icon: ShieldCheck },
];

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (value: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      className={`toggle${checked ? " active" : ""}`}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
    >
      <span />
    </button>
  );
}

export function SettingsDrawer(props: SettingsDrawerProps) {
  const [tab, setTab] = useState<SettingsTab>("general");
  const [joinCode, setJoinCode] = useState("");
  const [joinPasscode, setJoinPasscode] = useState("");
  const [showPasscode, setShowPasscode] = useState(false);
  const [accountName, setAccountName] = useState(props.account?.displayName ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!props.open) return;
    const onKeyDown = (event: KeyboardEvent) => event.key === "Escape" && props.onClose();
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [props.open, props.onClose]);

  useEffect(() => {
    if (!saved) return;
    const timer = window.setTimeout(() => setSaved(false), 1_300);
    return () => window.clearTimeout(timer);
  }, [saved]);

  useEffect(() => {
    if (props.open) setAccountName(props.account?.displayName ?? "");
  }, [props.account, props.open]);

  if (!props.open) return null;

  const updateSettings = (next: AppSettings) => {
    props.onSettings(next);
    setSaved(true);
  };

  const connect = async (action: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="settings-layer" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && props.onClose()}>
      <aside className="settings-drawer" role="dialog" aria-modal="true" aria-label="設定">
        <header className="settings-header">
          <div>
            <h2>設定</h2>
            <span className={`save-indicator${saved ? " visible" : ""}`}><Save size={13} /> 保存しました</span>
          </div>
          <button type="button" className="icon-button" onClick={props.onClose} aria-label="設定を閉じる"><X size={20} /></button>
        </header>
        <div className="settings-layout">
          <nav className="settings-nav" aria-label="設定カテゴリ">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button key={id} type="button" className={tab === id ? "active" : ""} onClick={() => setTab(id)}>
                <Icon size={17} />
                <span>{label}</span>
                <ChevronRight className="nav-chevron" size={14} />
              </button>
            ))}
          </nav>
          <div className="settings-content">
            {tab === "general" && (
              <section className="settings-section">
                <div className="section-heading"><h3>応答モード</h3><p>回答の速さや考え方を切り替えます。</p></div>
                <div className="mode-cards">
                  {(Object.keys(MODE_PROFILES) as Array<keyof typeof MODE_PROFILES>).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className={props.settings.mode === mode ? "active" : ""}
                      onClick={() => updateSettings({
                        ...props.settings,
                        mode,
                        temperature: MODE_PROFILES[mode].temperature,
                        maxNewTokens: MODE_PROFILES[mode].maxNewTokens,
                      })}
                    >
                      {mode === "fast" ? <Zap size={17} /> : mode === "thinking" ? <Sparkles size={17} /> : <CircleGauge size={17} />}
                      <strong>{MODE_PROFILES[mode].label}</strong>
                      <span>{MODE_PROFILES[mode].description}</span>
                    </button>
                  ))}
                </div>
                <div className="switch-row">
                  <div>
                    <strong>無料RAGを使う</strong>
                    <span>Wikipedia、Open-Meteo、RSSニュースを質問内容から自動取得します。API鍵は不要です。</span>
                  </div>
                  <Toggle
                    checked={props.settings.ragEnabled}
                    onChange={(ragEnabled) => updateSettings({ ...props.settings, ragEnabled })}
                    label="無料RAGを使う"
                  />
                </div>
                <label className="field-group">
                  <span>最大出力トークン <b>{props.settings.maxNewTokens}</b></span>
                  <input
                    type="range"
                    min="64"
                    max="2048"
                    step="64"
                    value={props.settings.maxNewTokens}
                    onChange={(event) => updateSettings({ ...props.settings, maxNewTokens: Number(event.target.value) })}
                  />
                </label>
                <label className="field-group">
                  <span>Temperature <b>{props.settings.temperature.toFixed(1)}</b></span>
                  <input
                    type="range"
                    min="0"
                    max="1.5"
                    step="0.1"
                    value={props.settings.temperature}
                    onChange={(event) => updateSettings({ ...props.settings, temperature: Number(event.target.value) })}
                  />
                </label>
              </section>
            )}

            {tab === "account" && (
              <section className="settings-section">
                <div className="section-heading">
                  <h3>アカウント</h3>
                  <p>ログイン済みアカウントです。tokenとパスワードの平文は保存しません。</p>
                </div>
                <div className="connection-card">
                  <div className="connection-status">
                    <span className={`status-dot ${props.account ? "online" : ""}`} />
                    {props.account ? "アカウント接続中" : "未作成"}
                  </div>
                  {props.account && (
                    <>
                      <div className="credential-row">
                        <div><span>ログインID</span><strong>{props.account.loginId ?? "legacy"}</strong></div>
                      </div>
                      <div className="credential-row">
                        <div><span>Account ID</span><strong>{props.account.id.slice(0, 8)}…</strong></div>
                      </div>
                    </>
                  )}
                </div>
                <label className="field-group">
                  <span>表示名</span>
                  <input
                    value={accountName}
                    maxLength={64}
                    onChange={(event) => setAccountName(event.target.value)}
                    placeholder="例：Webrain"
                  />
                </label>
                <div className="button-row">
                  <button
                    type="button"
                    className="button button--primary"
                    disabled={busy || !accountName.trim()}
                    onClick={() => void connect(async () => {
                      await props.onSaveAccount(accountName.trim());
                      setSaved(true);
                    })}
                  >
                    <Save size={16} /> 更新
                  </button>
                  {props.account && (
                    <button
                      type="button"
                      className="button button--danger"
                      disabled={busy}
                      onClick={() => void connect(async () => {
                        if (!window.confirm("このアカウント接続を削除します。会話履歴は残ります。")) return;
                        await props.onDeleteAccount();
                        setAccountName("");
                      })}
                    >
                      <Trash2 size={16} /> 削除
                    </button>
                  )}
                </div>
                {error && <div className="settings-error" role="alert">{error}</div>}
              </section>
            )}

            {tab === "context" && (
              <section className="settings-section">
                <div className="section-heading"><h3>コンテキスト</h3><p>長くするとメモリ使用量と待ち時間が増えます。</p></div>
                <label className="field-group">
                  <span>コンテキスト長</span>
                  <select
                    value={props.settings.contextTokens}
                    onChange={(event) => updateSettings({ ...props.settings, contextTokens: Number(event.target.value) as AppSettings["contextTokens"] })}
                  >
                    <option value="2048">2,048 tokens</option>
                    <option value="4096">4,096 tokens</option>
                    <option value="8192">8,192 tokens</option>
                  </select>
                </label>
                <label className="field-group">
                  <span>含める会話</span>
                  <select
                    value={props.settings.historyLimit}
                    onChange={(event) => updateSettings({ ...props.settings, historyLimit: Number(event.target.value) as AppSettings["historyLimit"] })}
                  >
                    <option value="6">直近6件</option>
                    <option value="12">直近12件</option>
                    <option value="20">直近20件</option>
                  </select>
                </label>
                <label className="field-group">
                  <span>常設コンテキスト</span>
                  <textarea
                    rows={5}
                    maxLength={20_000}
                    value={props.settings.customContext}
                    onChange={(event) => updateSettings({ ...props.settings, customContext: event.target.value })}
                    placeholder="プロジェクトの前提、用語、回答に含めたい背景を入力"
                  />
                  <small>{props.settings.customContext.length.toLocaleString()} / 20,000文字</small>
                </label>
                <label className="field-group">
                  <span>システムプロンプト（SYSPRMPT）</span>
                  <textarea
                    rows={7}
                    maxLength={20_000}
                    value={props.settings.systemPrompt}
                    onChange={(event) => updateSettings({ ...props.settings, systemPrompt: event.target.value })}
                    placeholder="AIの役割、口調、回答ルールを指定"
                  />
                  {props.model.promptAdapter === "deepseek" && (
                    <small className="inline-warning">DeepSeek公式推奨に合わせ、先頭のユーザー指示へ統合して渡します。</small>
                  )}
                </label>
              </section>
            )}

            {tab === "model" && (
              <section className="settings-section">
                <div className="section-heading"><h3>現在のAI</h3><p>用途に合わせて使うAIを選べます。</p></div>
                <div className="current-model-card">
                  <div className="model-symbol"><Cpu size={22} /></div>
                  <div><strong>{props.model.name}</strong><span>現在選択中</span></div>
                </div>
                {props.loadState.status === "loading" && (
                  <div className="download-progress" aria-live="polite">
                    <div><span>{props.loadState.message}</span><b>{Math.round(props.loadState.progress)}%</b></div>
                    <progress max="100" value={props.loadState.progress} />
                    {props.loadState.file && <small>{props.loadState.file}</small>}
                  </div>
                )}
                {props.loadState.status === "error" && <div className="settings-error">{props.loadState.message}</div>}
                <div className="button-row">
                  <button type="button" className="button button--secondary" onClick={props.onOpenModels}>AI一覧</button>
                  <button
                    type="button"
                    className="button button--primary"
                    onClick={props.onLoadModel}
                    disabled={!props.gpu?.available || props.loadState.status === "loading"}
                  >
                    <Download size={16} /> AIを準備
                  </button>
                </div>
                <div className="info-list">
                  <span><Gauge size={16} /> {props.gpu?.available ? "利用できます" : "確認中"}</span>
                  <span><Database size={16} /> AI設定: 自動</span>
                </div>
              </section>
            )}

            {tab === "swarm" && (
              <section className="settings-section">
                <div className="section-heading">
                  <h3>共有チャット</h3>
                  <p>必要なときだけ、招待コードでほかの環境とつなげます。</p>
                </div>
                {!props.swarm ? (
                  <>
                    <div className="swarm-scope-panel">
                      <button
                        type="button"
                        className={`scope-option${props.inferenceMode === "local" ? " active" : ""}`}
                        onClick={() => props.onInferenceMode("local")}
                      >
                        <Cpu size={17} />
                        <div><strong>通常</strong><span>このチャットだけで使います</span></div>
                      </button>
                      <button
                        type="button"
                        className={`scope-option${props.inferenceMode === "friend" ? " active" : ""}`}
                        onClick={() => props.onInferenceMode("friend")}
                      >
                        <Users size={17} />
                        <div><strong>招待共有</strong><span>コードとPassCodeでつなぎます</span></div>
                      </button>
                      <button
                        type="button"
                        className={`scope-option${props.inferenceMode === "world" ? " active" : ""}`}
                        onClick={() => props.onInferenceMode("world")}
                      >
                        <Network size={17} />
                        <div><strong>公開共有</strong><span>許可した環境だけ候補になります</span></div>
                      </button>
                    </div>
                    <label className="field-group">
                      <span>表示名</span>
                      <input
                        value={props.deviceName}
                        maxLength={32}
                        onChange={(event) => props.onDeviceName(event.target.value)}
                        placeholder="例：リビングPC"
                      />
                    </label>
                    <div className="join-box">
                      <h4><Users size={17} /> 新しいグループ</h4>
                      <p>招待コードと安全な8文字PassCodeを生成します。</p>
                      <button
                        type="button"
                        className="button button--primary button--full"
                        disabled={busy || props.inferenceMode === "local" || !props.deviceName.trim()}
                        onClick={() => void connect(() => props.onCreateSwarm(props.deviceName.trim()))}
                      >
                        {busy ? <RefreshCw className="spin" size={16} /> : <Network size={16} />} グループを作成
                      </button>
                    </div>
                    <div className="divider"><span>または参加</span></div>
                    <div className="join-grid">
                      <label className="field-group">
                        <span>招待コード</span>
                        <input
                          value={formatSwarmCode(joinCode)}
                          onChange={(event) => setJoinCode(normalizeSwarmCode(event.target.value))}
                          placeholder="AB12-CD34"
                          autoCapitalize="characters"
                        />
                      </label>
                      <label className="field-group">
                        <span>PassCode</span>
                        <div className="password-field">
                          <input
                            type={showPasscode ? "text" : "password"}
                            value={joinPasscode}
                            maxLength={8}
                            onChange={(event) => setJoinPasscode(event.target.value)}
                            placeholder="8文字"
                          />
                          <button type="button" onClick={() => setShowPasscode(!showPasscode)} aria-label="PassCodeの表示切替">
                            {showPasscode ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                        <button type="button" className="text-button" onClick={() => setJoinPasscode(generatePasscode())}>安全なPassCodeを生成</button>
                      </label>
                    </div>
                    <button
                      type="button"
                      className="button button--secondary button--full"
                      disabled={busy || props.inferenceMode === "local" || joinCode.length !== 8 || joinPasscode.length !== 8 || !props.deviceName.trim()}
                      onClick={() => void connect(() => props.onJoinSwarm(joinCode, joinPasscode, props.deviceName.trim()))}
                    >
                      <Link2 size={16} /> 参加する
                    </button>
                  </>
                ) : (
                  <>
                    <div className="connection-card">
                      <div className="connection-status"><span className="status-dot online" /> 共有チャット接続中</div>
                      <div className="credential-row">
                        <div><span>モード</span><strong>{props.inferenceMode === "local" ? "通常" : props.inferenceMode === "world" ? "公開共有" : "招待共有"}</strong></div>
                      </div>
                      <div className="credential-row">
                        <div><span>招待コード</span><strong>{formatSwarmCode(props.swarm.code)}</strong></div>
                        <button type="button" className="icon-button" onClick={() => void navigator.clipboard.writeText(props.swarm!.code)} aria-label="招待コードをコピー"><Copy size={17} /></button>
                      </div>
                      <div className="credential-row">
                        <div><span>PassCode</span><strong>{showPasscode ? props.swarm.passcode : "••••••••"}</strong></div>
                        <div className="credential-actions">
                          <button type="button" className="icon-button" onClick={() => setShowPasscode(!showPasscode)} aria-label="PassCodeの表示切替">{showPasscode ? <EyeOff size={17} /> : <Eye size={17} />}</button>
                          <button type="button" className="icon-button" onClick={() => void navigator.clipboard.writeText(props.swarm!.passcode)} aria-label="PassCodeをコピー"><Copy size={17} /></button>
                        </div>
                      </div>
                    </div>
                    <div className="swarm-scope-panel">
                      <button
                        type="button"
                        className={`scope-option${props.inferenceMode === "local" ? " active" : ""}`}
                        onClick={() => props.onInferenceMode("local")}
                      >
                        <Cpu size={17} />
                        <div><strong>通常</strong><span>このチャットだけで使います</span></div>
                      </button>
                      <button
                        type="button"
                        className={`scope-option${props.inferenceMode === "friend" ? " active" : ""}`}
                        onClick={() => props.onInferenceMode("friend")}
                      >
                        <Users size={17} />
                        <div><strong>招待共有</strong><span>コードとPassCodeの範囲だけで使います</span></div>
                      </button>
                      <button
                        type="button"
                        className={`scope-option${props.inferenceMode === "world" ? " active" : ""}`}
                        onClick={() => props.onInferenceMode("world")}
                      >
                        <Network size={17} />
                        <div><strong>公開共有</strong><span>許可した環境だけ候補になります</span></div>
                      </button>
                    </div>
                    <div className="switch-row">
                      <div><strong>共有を許可</strong><span>共有モードのときだけ有効になります</span></div>
                      <Toggle checked={props.contributing} onChange={props.onContributing} label="共有を許可" />
                    </div>
                    <div className="latency-grid">
                      <div><span>接続状態</span><strong>{props.signalingRttMs === null ? "確認中" : "良好"}</strong></div>
                      <div><span>TURN</span><strong>{props.turnConfigured ? "設定済み" : "STUNのみ"}</strong></div>
                      <div><span>接続数</span><strong>{props.peers.filter((peer) => peer.dataChannelState === "open").length}</strong></div>
                    </div>
                    <div className="peer-list">
                      <h4>接続中</h4>
                      {props.peers.length ? props.peers.map((peer) => (
                        <div key={peer.id} className="peer-row">
                          <div className="peer-icon"><Wifi size={16} /></div>
                          <div><strong>{peer.name}</strong><span>参加中</span></div>
                          <span className={`peer-state ${peer.dataChannelState === "open" ? "ready" : "waiting"}`}>
                            {peer.dataChannelState === "open" ? `${peer.rttMs?.toFixed(0) ?? "—"} ms` : "接続中"}
                          </span>
                        </div>
                      )) : <p className="muted-empty">別の環境からコードとPassCodeで参加してください。</p>}
                    </div>
                    <button type="button" className="button button--danger button--full" disabled={busy} onClick={() => void connect(props.onLeaveSwarm)}><LogOut size={16} /> グループを退出</button>
                  </>
                )}
                {error && <div className="settings-error" role="alert">{error}</div>}
              </section>
            )}

            {tab === "acceleration" && (
              <section className="settings-section">
                <div className="section-heading"><h3>詳細設定</h3><p>回答品質と速度に関する詳細項目です。</p></div>
                <div className="experiment-list">
                  <article className="experiment-active"><span className="experiment-letter">A</span><div><strong>高速応答</strong><p>短い質問にはすばやく返答します。</p><em><Check size={13} /> 有効</em></div></article>
                  <article className="experiment-active"><span className="experiment-letter">B</span><div><strong>安定化</strong><p>失敗時は通常の回答に切り替えます。</p><em><Check size={13} /> 有効</em></div></article>
                  <article><span className="experiment-letter">C</span><div><strong>品質調整</strong><p>回答モードに合わせて出力を調整します。</p><em>自動</em></div></article>
                </div>
                <div className="lab-disclaimer"><Activity size={16} /> 実際の速度は利用状況によって変わります。</div>
              </section>
            )}

            {tab === "privacy" && (
              <section className="settings-section">
                <div className="section-heading"><h3>データと安全性</h3><p>安心して使えるように基本設定をまとめています。</p></div>
                <div className="privacy-flow">
                  <div><Cpu size={18} /><strong>通常チャット</strong><span>AIに質問できます</span></div>
                  <div><Network size={18} /><strong>共有</strong><span>必要なときだけ招待できます</span></div>
                  <div><Database size={18} /><strong>履歴</strong><span>会話の管理に使います</span></div>
                </div>
                <ul className="safety-list">
                  <li><Check size={15} /> PassCode平文を保存しない</li>
                  <li><Check size={15} /> ログイン状態は安全に管理</li>
                  <li><Check size={15} /> ファイルは回答のために必要な範囲で利用</li>
                  <li><Check size={15} /> 不要な外部接続を抑制</li>
                </ul>
                <div className="danger-zone">
                  <div><strong>履歴を消去</strong><span>保存した会話を削除します。</span></div>
                  <button type="button" className="button button--danger" onClick={() => void props.onClearData()}><Trash2 size={16} /> 履歴を消去</button>
                </div>
              </section>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
